import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  authService,
  setApiErrorHandlers,
  clearApiErrorHandlers,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_USER_KEY,
  AUTH_LOGGED_IN_KEY,
} from '../api';
import { registerBannedSyncHandler } from '../api/client/bannedSync';
import { readFriendlyApiErrorMessage, getFriendlyApiErrorMessage } from '../utils/apiErrorMessages';
import type { UserViewModel } from '../api/types/auth';
import type { CurrentUserViewModel } from '../api/types/auth';
import type { RegisterRequest } from '../api/types/auth';
import type { CorporateUserRegisterRequest } from '../api/types/auth';
import type { CorporateApprovalStatus } from '../api/types/corporate';

/** User shape exposed by context – compatible with API UserViewModel and auth/me. */
export interface AuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  /** Display name: "firstName lastName" or email. */
  name: string;
  /** Role names from login and auth/me (e.g. "Admin", "Company Admin", "Super Admin"). */
  roles: string[];
  phoneNumber: string | null;
  description: string | null;
  profilePictureUrl: string | null;
  isCorporate?: boolean;
  corporateApprovalStatus?: CorporateApprovalStatus | null;
  isBanned?: boolean;
  banReason?: string | null;
  bannedAt?: string | null;
}

/** Map API user (login or auth/me) to AuthUser. */
function toAuthUser(
  v: (UserViewModel | CurrentUserViewModel) | null
): AuthUser | null {
  if (!v) return null;
  const name =
    [v.firstName, v.lastName].filter(Boolean).join(' ') ||
    (v.email ?? '');
  const profilePictureUrl = (v as { profilePictureUrl?: string | null }).profilePictureUrl ?? null;
  const me = v as CurrentUserViewModel & {
    isBanned?: boolean;
    banReason?: string | null;
    bannedAt?: string | null;
    isCorporate?: boolean;
    corporateApprovalStatus?: CorporateApprovalStatus | null;
  };
  return {
    id: v.id,
    email: v.email ?? null,
    firstName: v.firstName ?? null,
    lastName: v.lastName ?? null,
    name,
    roles: Array.isArray(v.roles) ? v.roles : [],
    phoneNumber: v.phoneNumber ?? null,
    description: v.description ?? null,
    profilePictureUrl: profilePictureUrl ?? null,
    isCorporate: me.isCorporate === true,
    corporateApprovalStatus: me.corporateApprovalStatus ?? null,
    isBanned: me.isBanned === true,
    banReason: me.banReason ?? null,
    bannedAt: me.bannedAt ?? null,
  };
}

function loadStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserViewModel & {
      isBanned?: boolean;
      banReason?: string | null;
      bannedAt?: string | null;
      isCorporate?: boolean;
      corporateApprovalStatus?: CorporateApprovalStatus | null;
    };
    return toAuthUser(parsed);
  } catch {
    return null;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (payload: RegisterRequest) => Promise<void>;
  registerCorporate: (payload: CorporateUserRegisterRequest) => Promise<void>;
  loginWithGoogle: (token: string, refreshToken: string) => Promise<void>;
  /** Refresh user from GET /api/auth/me (e.g. after profile picture update). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem(AUTH_LOGGED_IN_KEY) === 'true';
  });
  const [user, setUser] = useState<AuthUser | null>(() => loadStoredUser());

  const clearAuthState = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_LOGGED_IN_KEY);
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  useEffect(() => {
    setApiErrorHandlers({
      onUnauthorized: (message?: string) => {
        clearAuthState();
        navigate('/giris', { state: message ? { sessionEndedMessage: message } : undefined });
      },
    });
    return () => {
      clearApiErrorHandlers();
    };
  }, [clearAuthState, navigate]);

  /** Hydrate user (and roles) from auth/me when already authenticated (e.g. page refresh). */
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    authService
      .getMe()
      .then((result) => {
        if (cancelled || !result.isSuccess || !result.data) return;
        setUser(toAuthUser(result.data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      localStorage.setItem(AUTH_LOGGED_IN_KEY, 'true');
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          isEmailConfirmed: true,
          reportCount: 0,
          roles: user.roles,
          phoneNumber: user.phoneNumber,
          description: user.description,
          profilePictureUrl: user.profilePictureUrl ?? null,
          isCorporate: user.isCorporate === true,
          corporateApprovalStatus: user.corporateApprovalStatus ?? null,
          isBanned: user.isBanned === true,
          banReason: user.banReason ?? null,
          bannedAt: user.bannedAt ?? null,
        })
      );
    }
  }, [isAuthenticated, user]);


  const refreshUser = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) return;
    const result = await authService.getMe();
    if (result.isSuccess && result.data) {
      setUser(toAuthUser(result.data));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    registerBannedSyncHandler(() => {
      void refreshUser();
    });
    return () => registerBannedSyncHandler(null);
  }, [refreshUser]);

  const loginWithGoogle = useCallback(async (token: string, refreshToken: string): Promise<void> => {
    try {
      console.log(token, refreshToken);
      if (token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
      }
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      const result = await authService.getMe();
      if (result.isSuccess && result.data) {
        setUser(toAuthUser(result.data));
        setIsAuthenticated(true);
        navigate('/');
      } else {
        throw new Error('Giriş başarısız. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }, [navigate]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const redirectToEmailDogrula = (message: string) => {
        navigate('/email-dogrula', {
          state: { email, message },
        });
      };

      try {
        const result = await authService.login({ email, password });
        if (result.statusCode === 412) {
          redirectToEmailDogrula(
            result.message ?? 'E-posta adresiniz henüz doğrulanmamış.'
          );
          return;
        }
        if (!result.isSuccess || !result.data) {
          throw new Error(
            getFriendlyApiErrorMessage(
              new Error(result.message ?? 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.'),
              'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.'
            )
          );
        }
        const authToken = result.data.authToken ?? result.data.token;
        const { refreshToken, user: apiUser, corporateApprovalStatus, isCorporateUser } = result.data;
        if (authToken) {
          localStorage.setItem(AUTH_TOKEN_KEY, authToken);
        }
        if (refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }
        let authUser = toAuthUser(apiUser ?? null);
        if (authUser && isCorporateUser) {
          authUser = {
            ...authUser,
            isCorporate: true,
            corporateApprovalStatus: corporateApprovalStatus ?? authUser.corporateApprovalStatus ?? null,
          };
        }
        if (!authUser?.id) {
          const meResult = await authService.getMe();
          if (meResult.isSuccess && meResult.data) {
            authUser = toAuthUser(meResult.data);
          }
        }
        setUser(authUser);
        setIsAuthenticated(true);
        navigate('/');
      } catch (err: unknown) {
        const res = err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number; data?: { message?: string } } }).response
          : undefined;
        if (res?.status === 412) {
          redirectToEmailDogrula(
            res.data?.message ?? 'E-posta adresiniz henüz doğrulanmamış.'
          );
          return;
        }
        if (res?.status === 403) {
          throw new Error(readFriendlyApiErrorMessage(err, 'Kurumsal hesabınız onaylanmadı.'));
        }
        throw new Error(readFriendlyApiErrorMessage(err, 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.'));
      }
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthState();
      navigate('/');
    }
  }, [clearAuthState, navigate]);

  const register = useCallback(
    async (payload: RegisterRequest): Promise<void> => {
      const result = await authService.register(payload);
      if (!result.isSuccess) {
        const message = result.message ?? 'Kayıt başarısız. Lütfen tekrar deneyin.';
        throw new Error(message);
      }
      navigate('/email-dogrula');
    },
    [navigate]
  );

  const registerCorporate = useCallback(
    async (payload: CorporateUserRegisterRequest): Promise<void> => {
      const result = await authService.registerCorporate(payload);
      if (!result.isSuccess) {
        const message = result.message ?? 'Kayıt başarısız. Lütfen tekrar deneyin.';
        throw new Error(message);
      }
      navigate('/email-dogrula');
    },
    [navigate]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        register,
        registerCorporate,
        loginWithGoogle,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  }
  return context;
};

/** Role names that grant access to the admin panel. */
const ADMIN_PANEL_ROLES = ['Admin', 'Company Admin', 'Super Admin'] as const;

/** True if the user has a role that can access the admin panel. */
export function hasAdminPanelAccess(user: AuthUser | null): boolean {
  if (!user?.roles?.length) return false;
  const roleSet = new Set(ADMIN_PANEL_ROLES);
  return user.roles.some((r) => roleSet.has(r as (typeof ADMIN_PANEL_ROLES)[number]));
}

/** Role name required to read the admin audit log (011, US3). */
const SUPER_ADMIN_ROLE = 'Super Admin';

/** True if the user has the Super Admin role — gate for the admin audit log. */
export function hasSuperAdminAccess(user: AuthUser | null): boolean {
  return !!user?.roles?.includes(SUPER_ADMIN_ROLE);
}
